import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { RewardsTier } from '../types'

export default function RewardsProgram() {
  const { isLoggedIn } = useAuth()
  const [activeSection, setActiveSection] = useState<'overview' | 'tiers' | 'faq'>('overview')

  const rewardsTiers: RewardsTier[] = [
    {
      name: 'bronze',
      minReferrals: 0,
      referralBonus: 500,
      purchaseCashbackPercent: 1,
      reviewReward: 100,
      tierBenefits: [
        '500 points welcome bonus',
        '500 points per successful referral',
        '1% cashback from every purchase with partnered vendors',
        '100 points for verified reviews'
      ]
    },
    {
      name: 'silver',
      minReferrals: 5,
      referralBonus: 1000,
      purchaseCashbackPercent: 2,
      reviewReward: 250,
      tierBenefits: [
        '1000 points per successful referral',
        '2% cashback from every purchase with partnered vendors',
        '250 points for verified reviews',
        'Early access to sales',
        'Priority customer support'
      ]
    },
    {
      name: 'gold',
      minReferrals: 15,
      referralBonus: 1500,
      purchaseCashbackPercent: 3,
      reviewReward: 500,
      tierBenefits: [
        '1500 points per successful referral',
        '3% cashback from every purchase with partnered vendors',
        '500 points for verified reviews',
        'Exclusive member discounts',
        'Monthly bonus rewards',
        'VIP customer support'
      ]
    },
    {
      name: 'platinum',
      minReferrals: 50,
      referralBonus: 2500,
      purchaseCashbackPercent: 5,
      reviewReward: 1000,
      tierBenefits: [
        '2500 points per successful referral',
        '5% cashback from every purchase with partnered vendors',
        '1000 points for verified reviews',
        'Maximum tier rewards',
        'Exclusive platinum perks',
        'Personal account manager',
        'Beta access to new features'
      ]
    }
  ]

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'bronze': return '#CD7F32'
      case 'silver': return '#C0C0C0'
      case 'gold': return '#FFD700'
      case 'platinum': return '#E5E4E2'
      default: return '#CD7F32'
    }
  }

  const formatPoints = (amount: number) => {
    return `${amount.toLocaleString()} points`
  }

  return (
    <div className="rewards-program">
      <div className="program-header">
        <h1>Rewards Program</h1>
        <p>Earn cash rewards for every referral, purchase, and review!</p>
        {!isLoggedIn && (
          <div className="signup-cta">
            <p>Join today and get a <strong>$5 welcome bonus</strong>!</p>
            <button className="signup-btn">Sign Up Now</button>
          </div>
        )}
      </div>

      <div className="program-tabs">
        <button 
          className={`tab-btn ${activeSection === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSection('overview')}
        >
          How It Works
        </button>
        <button 
          className={`tab-btn ${activeSection === 'tiers' ? 'active' : ''}`}
          onClick={() => setActiveSection('tiers')}
        >
          Reward Tiers
        </button>
        <button 
          className={`tab-btn ${activeSection === 'faq' ? 'active' : ''}`}
          onClick={() => setActiveSection('faq')}
        >
          FAQ
        </button>
      </div>

      {activeSection === 'overview' && (
        <div className="overview-section">
          <div className="earning-methods">
            <h2>Ways to Earn Rewards</h2>
            <div className="points-conversion">
              <strong>💡 Points Value:</strong> 1000 points = $10.00 cash value
            </div>
            <div className="methods-grid">
              <div className="method-card">
                <div className="method-icon">👥</div>
                <h3>Refer Friends</h3>
                <p>Earn <strong>500-2500 points</strong> for every successful referral</p>
                <div className="method-details">
                  <ul>
                    <li>Share your unique referral code</li>
                    <li>Friend signs up and makes first purchase</li>
                    <li>Both of you earn rewards!</li>
                  </ul>
                </div>
              </div>

              <div className="method-card">
                <div className="method-icon">🛒</div>
                <h3>Make Purchases</h3>
                <p>Earn <strong>1-5% cashback</strong> on every order</p>
                <div className="method-details">
                  <ul>
                    <li>Automatic cashback on all purchases</li>
                    <li>Higher tiers = higher cashback</li>
                    <li>No minimum purchase required</li>
                  </ul>
                </div>
              </div>

              <div className="method-card">
                <div className="method-icon">⭐</div>
                <h3>Write Reviews</h3>
                <p>Earn <strong>100-1000 points</strong> for verified reviews</p>
                <div className="method-details">
                  <ul>
                    <li>Write honest, detailed reviews</li>
                    <li>Must be verified purchase</li>
                    <li>Help the community decide</li>
                  </ul>
                </div>
              </div>

              <div className="method-card">
                <div className="method-icon">🎁</div>
                <h3>Special Bonuses</h3>
                <p>Extra rewards for active members</p>
                <div className="method-details">
                  <ul>
                    <li>Monthly engagement bonuses</li>
                    <li>Holiday special rewards</li>
                    <li>Community milestone rewards</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="how-it-works">
            <h2>How the Referral Process Works</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Share Your Code</h4>
                  <p>Copy your unique referral code or link and share it with friends</p>
                </div>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>Friend Signs Up</h4>
                  <p>Your friend creates an account using your referral code</p>
                </div>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>First Purchase</h4>
                  <p>When they make their first purchase, rewards are unlocked</p>
                </div>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4>Both Earn Rewards</h4>
                  <p>You both receive cash rewards in your accounts</p>
                </div>
              </div>
            </div>
          </div>

          <div className="redemption-info">
            <h2>Redeem Your Rewards</h2>
            <div className="redemption-options">
              <div className="option">
                <h4>💳 Gift Cards</h4>
                <p>Redeem for popular retailer gift cards (minimum $10)</p>
              </div>
              <div className="option">
                <h4>💰 Cash</h4>
                <p>Direct deposit to your bank account (minimum $25)</p>
              </div>
              <div className="option">
                <h4>🛍️ Store Credit</h4>
                <p>Use rewards as credit for vendor purchases (no minimum)</p>
              </div>
              <div className="option">
                <h4>🎁 Exclusive Items</h4>
                <p>Redeem for exclusive merchandise and special offers</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'tiers' && (
        <div className="tiers-section">
          <h2>Reward Tiers</h2>
          <p>Level up your rewards by referring more friends!</p>
          
          <div className="tiers-grid">
            {rewardsTiers.map((tier) => (
              <div key={tier.name} className="tier-card" style={{ borderColor: getTierColor(tier.name) }}>
                <div className="tier-header" style={{ background: getTierColor(tier.name) }}>
                  <h3>{tier.name.toUpperCase()}</h3>
                  <div className="tier-requirement">
                    {tier.minReferrals === 0 ? 'Starting Tier' : `${tier.minReferrals}+ Referrals`}
                  </div>
                </div>
                
                <div className="tier-rewards">
                  <div className="reward-item">
                    <span className="reward-label">Referral Bonus</span>
                    <span className="reward-value">{formatPoints(tier.referralBonus)}</span>
                  </div>
                  <div className="reward-item">
                    <span className="reward-label">Purchase Cashback</span>
                    <span className="reward-value">{tier.purchaseCashbackPercent}%</span>
                  </div>
                  <div className="reward-item">
                    <span className="reward-label">Review Reward</span>
                    <span className="reward-value">{formatPoints(tier.reviewReward)}</span>
                  </div>
                </div>

                <div className="tier-benefits">
                  <h4>Benefits</h4>
                  <ul>
                    {tier.tierBenefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSection === 'faq' && (
        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-list">
            <div className="faq-item">
              <h4>When do I receive my referral rewards?</h4>
              <p>Referral rewards are credited to your account within 24-48 hours after your referred friend makes their first purchase.</p>
            </div>

            <div className="faq-item">
              <h4>Is there a limit to how many people I can refer?</h4>
              <p>No! There's no limit to the number of people you can refer. The more you refer, the higher your tier and the better your rewards.</p>
            </div>

            <div className="faq-item">
              <h4>How do I track my referrals?</h4>
              <p>Your referral dashboard shows all your referrals, their status, and your earnings. You can access it anytime from your account menu.</p>
            </div>

            <div className="faq-item">
              <h4>What qualifies as a "successful referral"?</h4>
              <p>A successful referral is when someone signs up using your code and makes their first purchase within 30 days of registration.</p>
            </div>

            <div className="faq-item">
              <h4>Can I refer someone who already has an account?</h4>
              <p>No, referral codes only work for new users who don't already have an account with us.</p>
            </div>

            <div className="faq-item">
              <h4>Do my rewards expire?</h4>
              <p>Rewards don't expire, but you must remain an active member. Accounts inactive for 12+ months may have rewards suspended.</p>
            </div>

            <div className="faq-item">
              <h4>How do tier upgrades work?</h4>
              <p>Your tier is automatically upgraded based on your total successful referrals. Benefits apply immediately upon reaching the new tier.</p>
            </div>

            <div className="faq-item">
              <h4>What happens if my referred friend returns their purchase?</h4>
              <p>If a purchase that triggered referral rewards is returned, the rewards may be deducted from both accounts.</p>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .rewards-program {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          background: #1a202c;
          min-height: 100vh;
          color: #e2e8f0;
        }

        .program-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .program-header h1 {
          color: #00d166;
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .program-header p {
          font-size: 1.25rem;
          color: #a0aec0;
          margin-bottom: 2rem;
        }

        .signup-cta {
          background: linear-gradient(135deg, #00d166, #4caf50);
          padding: 2rem;
          border-radius: 12px;
          margin: 2rem auto;
          max-width: 500px;
        }

        .signup-cta p {
          color: white;
          margin: 0 0 1rem 0;
          font-size: 1.1rem;
        }

        .signup-btn {
          background: #2d3748;
          color: #00d166;
          border: none;
          padding: 1rem 2rem;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s;
        }

        .signup-btn:hover {
          transform: translateY(-2px);
        }

        .program-tabs {
          display: flex;
          gap: 1rem;
          margin-bottom: 3rem;
          border-bottom: 1px solid #4a5568;
          justify-content: center;
        }

        .tab-btn {
          background: none;
          border: none;
          color: #a0aec0;
          padding: 1rem 2rem;
          cursor: pointer;
          font-size: 1.1rem;
          font-weight: 500;
          border-bottom: 2px solid transparent;
          transition: all 0.2s;
        }

        .tab-btn:hover {
          color: #e2e8f0;
        }

        .tab-btn.active {
          color: #00d166;
          border-bottom-color: #00d166;
        }

        .earning-methods h2 {
          text-align: center;
          color: #e2e8f0;
          margin-bottom: 2rem;
          font-size: 2rem;
        }

        .methods-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          margin-bottom: 4rem;
        }

        .method-card {
          background: #2d3748;
          border: 1px solid #4a5568;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .method-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 209, 102, 0.1);
        }

        .method-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .method-card h3 {
          color: #00d166;
          margin-bottom: 0.5rem;
          font-size: 1.5rem;
        }

        .method-card p {
          color: #a0aec0;
          margin-bottom: 1.5rem;
          font-size: 1.1rem;
        }

        .method-details ul {
          text-align: left;
          color: #cbd5e0;
          padding-left: 1.5rem;
        }

        .method-details li {
          margin-bottom: 0.5rem;
        }

        .how-it-works {
          margin-bottom: 4rem;
        }

        .how-it-works h2 {
          text-align: center;
          color: #e2e8f0;
          margin-bottom: 3rem;
          font-size: 2rem;
        }

        .process-steps {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .step {
          background: #2d3748;
          border: 1px solid #4a5568;
          border-radius: 12px;
          padding: 2rem;
          text-align: center;
          max-width: 200px;
          flex: 1;
          min-width: 180px;
        }

        .step-number {
          background: #00d166;
          color: white;
          width: 3rem;
          height: 3rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0 auto 1rem auto;
        }

        .step-content h4 {
          color: #e2e8f0;
          margin-bottom: 0.5rem;
        }

        .step-content p {
          color: #a0aec0;
          font-size: 0.9rem;
        }

        .step-arrow {
          font-size: 2rem;
          color: #00d166;
          font-weight: bold;
        }

        .redemption-info h2 {
          text-align: center;
          color: #e2e8f0;
          margin-bottom: 2rem;
          font-size: 2rem;
        }

        .redemption-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .option {
          background: #2d3748;
          border: 1px solid #4a5568;
          border-radius: 8px;
          padding: 1.5rem;
          text-align: center;
        }

        .option h4 {
          color: #00d166;
          margin-bottom: 0.5rem;
          font-size: 1.25rem;
        }

        .option p {
          color: #a0aec0;
          margin: 0;
        }

        .tiers-section h2 {
          text-align: center;
          color: #e2e8f0;
          margin-bottom: 1rem;
          font-size: 2rem;
        }

        .tiers-section p {
          text-align: center;
          color: #a0aec0;
          margin-bottom: 3rem;
          font-size: 1.1rem;
        }

        .tiers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .tier-card {
          background: #2d3748;
          border: 2px solid;
          border-radius: 12px;
          overflow: hidden;
          transition: transform 0.2s;
        }

        .tier-card:hover {
          transform: translateY(-4px);
        }

        .tier-header {
          color: #1a202c;
          padding: 1.5rem;
          text-align: center;
        }

        .tier-header h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
          font-weight: bold;
        }

        .tier-requirement {
          font-weight: 600;
          opacity: 0.8;
        }

        .tier-rewards {
          padding: 1.5rem;
          border-bottom: 1px solid #4a5568;
        }

        .reward-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .reward-label {
          color: #a0aec0;
          font-size: 0.9rem;
        }

        .reward-value {
          color: #00d166;
          font-weight: bold;
          font-size: 1.1rem;
        }

        .tier-benefits {
          padding: 1.5rem;
        }

        .tier-benefits h4 {
          color: #e2e8f0;
          margin: 0 0 1rem 0;
        }

        .tier-benefits ul {
          color: #cbd5e0;
          padding-left: 1.5rem;
          margin: 0;
        }

        .tier-benefits li {
          margin-bottom: 0.5rem;
          font-size: 0.9rem;
        }

        .faq-section h2 {
          text-align: center;
          color: #e2e8f0;
          margin-bottom: 3rem;
          font-size: 2rem;
        }

        .faq-list {
          max-width: 800px;
          margin: 0 auto;
        }

        .faq-item {
          background: #2d3748;
          border: 1px solid #4a5568;
          border-radius: 8px;
          padding: 2rem;
          margin-bottom: 1.5rem;
        }

        .faq-item h4 {
          color: #00d166;
          margin: 0 0 1rem 0;
          font-size: 1.25rem;
        }

        .faq-item p {
          color: #cbd5e0;
          margin: 0;
          line-height: 1.6;
        }

        @media (max-width: 768px) {
          .rewards-program {
            padding: 1rem;
          }

          .program-header h1 {
            font-size: 2rem;
          }

          .program-tabs {
            flex-direction: column;
          }

          .process-steps {
            flex-direction: column;
          }

          .step-arrow {
            transform: rotate(90deg);
          }

          .methods-grid, .tiers-grid, .redemption-options {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  )
}